var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { sidebarWidthPx } from "ui/layout-constants";
import * as Styled from "ui/components/panels/sidebar/sidebar.style";
import { getTubeConnectionImage, tubeConnectionModels, } from "3d/constants/common";
import { useAppDispatch, actions, selectors, useAppSelector } from "store";
import { KeyBindings, useKeyBindings } from "hooks/keybindings";
import { useNumberConversionWithMillimeters } from "hooks/number-conversion";
import { Unit } from "utility/number";
import { MultipleValuesCustomSlider } from "ui/components/elements/custom-slider/multiple-values-custom-slider";
import { SidebarDrawerButton } from "ui/components/elements/custom-drawer";
import { ComponentSection } from "ui/components/panels/canvas/node-components/component-select/component-section";
import { ComponentSelect } from "ui/components/panels/canvas/node-components/component-select/component-select";
var TubeDiameters = function (_a) {
    var tubeConnection = _a.tubeConnection;
    var convertNumberWithMillimeters = useNumberConversionWithMillimeters();
    var tubeDiameters = useMemo(function () {
        var tubeDiameters = __spreadArray([], __read(tubeConnection.diameters), false);
        // Sort tubeDiameters array so we get the largest diameter first
        tubeDiameters.sort(function (a, b) { return Number(b) - Number(a); });
        return tubeDiameters.map(function (diameter) { return convertNumberWithMillimeters(diameter, Unit.M).formatted; });
    }, [convertNumberWithMillimeters, tubeConnection.diameters]);
    return (_jsx(Styled.TubeDiameterContainer, { children: tubeDiameters.map(function (diameter, i) { return (_jsx(Styled.Diameter, { children: diameter }, i)); }) }));
};
var supportedKeyboardKeys = [KeyBindings.ESCAPE];
var tubeConnectionDrawerId = "tube-connection-drawer";
export var TubeConnectionMenu = function (_a) {
    var _b;
    var nodes = _a.nodes;
    var t = useTranslation().t;
    var dispatch = useAppDispatch();
    var convertNumberWithMillimeters = useNumberConversionWithMillimeters();
    var isMultiSelecting = useAppSelector(selectors.selectIsMultiSelecting);
    var activeDrawerItem = useAppSelector(selectors.selectActiveSidebarItemID);
    var tubeConnections = nodes.map(function (node) { return node.component; });
    var toolchangerDiameters = tubeConnections.map(function (tubeConnection) { return tubeConnection.diameters[0]; });
    /* Placeholders (no connection type picked yet) have no model. */
    var pickedTypes = tubeConnections
        .map(function (tubeConnection) { return tubeConnection.type; })
        .filter(function (type) { return type !== undefined; });
    var models = pickedTypes.map(function (type) { return tubeConnectionModels[type]; });
    var isPlaceholder = pickedTypes.length === 0;
    useKeyBindings({ keys: supportedKeyboardKeys });
    /* Selecting a tube connection, or adding one (which selects it), opens the
     * connection type drawer right away. The ref keeps it to one auto-open per
     * tube connection, so the drawer stays closed if the user closes it. */
    var nodeId = nodes[0].id;
    var autoOpenedForNodeId = useRef();
    useEffect(function () {
        if (isMultiSelecting || autoOpenedForNodeId.current === nodeId)
            return;
        autoOpenedForNodeId.current = nodeId;
        dispatch(actions.setActiveSidebarItemID(tubeConnectionDrawerId));
    }, [nodeId, isMultiSelecting, dispatch]);
    var onSelectType = function (type) {
        var typeId = type;
        dispatch(actions.setTubeConnectionType({
            nodeIds: nodes.map(function (node) { return node.id; }),
            type: typeId,
        }));
    };
    var onChangeDiameter = function (diameter) {
        dispatch(actions.setTubeConnectionDiameters({
            nodeIds: nodes.map(function (node) { return node.id; }),
            diameters: [diameter / 1000],
        }));
    };
    var tubeConnectionOptions = useMemo(function () {
        var items = Object.keys(tubeConnectionModels);
        var labels = items.map(function (item) {
            return t(tubeConnectionModels[item].translationKey);
        });
        var images = items.map(getTubeConnectionImage);
        return { items: items, labels: labels, images: images };
    }, [t]);
    var isToolchanger = tubeConnections.every(function (tubeConnection) {
        return tubeConnection.type !== undefined &&
            tubeConnectionModels[tubeConnection.type].type === "tool-changer";
    });
    var diameterLabel = useMemo(function () {
        if (isToolchanger) {
            return t("sidebar.edit_tube_connection.connection_diameter_toolchanger");
        }
        var hasMultipleDiameters = tubeConnections.some(function (tubeConnection) { return tubeConnection.diameters.length > 1; });
        if (hasMultipleDiameters) {
            return t("sidebar.edit_tube_connection.connection_diameters");
        }
        return t("sidebar.edit_tube_connection.connection_diameter");
    }, [isToolchanger, t, tubeConnections]);
    // the largest minimum diameter of all models
    var toolChangers = models.filter(function (model) { return model.type === "tool-changer"; });
    var diameterMin = toolChangers.length > 0
        ? toolChangers.reduce(function (min, model) { return Math.max(min, model.diameterRange[0]); }, toolChangers[0].diameterRange[0])
        : 0;
    // the smallest maximum diameter of all models
    var diameterMax = toolChangers.length > 0
        ? toolChangers.reduce(function (max, model) { return Math.min(max, model.diameterRange[1]); }, toolChangers[0].diameterRange[1])
        : 0;
    var toolChangerLabel = __spreadArray([], __read(new Set(toolchangerDiameters)), false).sort(function (a, b) { return a - b; })
        .map(function (diameter) { return convertNumberWithMillimeters(diameter, Unit.M).formatted; })
        .join(", ");
    return (_jsxs(_Fragment, { children: [_jsx(SidebarDrawerButton, { button: _jsx(ComponentSection, { header: t("sidebar.edit_tube_connection.connection_type_label"), selected: activeDrawerItem === tubeConnectionDrawerId, onClick: function (e) {
                        e.stopPropagation();
                        dispatch(actions.setActiveSidebarItemID(activeDrawerItem === tubeConnectionDrawerId
                            ? undefined
                            : tubeConnectionDrawerId));
                    }, value: ((_b = tubeConnections[0]) === null || _b === void 0 ? void 0 : _b.type)
                        ? t(tubeConnectionModels[tubeConnections[0].type]
                            .translationKey)
                        : t("sidebar.edit_tube_connection.no_connection_type_selected") }), id: tubeConnectionDrawerId, keepSceneLive: true, sidebarWidth: sidebarWidthPx, customRenderElement: _jsx(ComponentSelect, { labels: tubeConnectionOptions.labels, values: tubeConnectionOptions.items, images: tubeConnectionOptions.images, selectedValues: pickedTypes, onValueChange: onSelectType, scrollIntoPosition: "nearest" }) }), isToolchanger ? (_jsx(Styled.SidebarSection, { children: _jsx(MultipleValuesCustomSlider, { header: diameterLabel, className: "".concat(Styled.prefix, "-slider"), min: diameterMin * 1000, max: diameterMax * 1000, stepSize: 0.5, showSteps: false, showValue: true, values: toolchangerDiameters.map(function (diameter) { return diameter * 1000; }), onValueChange: onChangeDiameter, labelLeft: toolChangerLabel }) })) : (!isMultiSelecting &&
                !isPlaceholder && (_jsx(Styled.SidebarSection, { children: _jsx(TubeDiameters, { tubeConnection: tubeConnections[0] }) })))] }));
};
